import { useCallback, useRef, useState } from "react"
import api from "../lib/api"
import { toast } from "../lib/toast"

const RETRY_DELAY_MS = 2000

export type CourseProgressEntry = {
  course_id: string
  completed_lessons: number
  total_lessons: number
}

type PutProgressResponse = {
  completed: boolean
  last_position_seconds: number
  watch_time_seconds: number
  max_scroll_percent: number | null
  courses?: CourseProgressEntry[]
}

export type CompletedLessonsState = Record<string, true>

type UseOptimisticLessonCompletionArgs = {
  /** Called synchronously the moment a lesson is optimistically marked done. */
  onOptimistic: (lessonId: string) => void
  /** Called once the server confirms — carries per-course aggregates when present. */
  onConfirmed: (lessonId: string, courses: CourseProgressEntry[]) => void
  /** Called to undo an optimistic mark that never got confirmed. */
  onRollback: (lessonId: string) => void
}

/**
 * Marks a lesson as completed instantly in the UI (like a social "like"
 * button), reconciling with the server in the background. See
 * openspec/changes/academy-optimistic-lesson-progress/design.md.
 */
export function useOptimisticLessonCompletion({
  onOptimistic,
  onConfirmed,
  onRollback,
}: UseOptimisticLessonCompletionArgs) {
  const [pendingLessonIds, setPendingLessonIds] = useState<Set<string>>(new Set())
  const inFlightRef = useRef<Set<string>>(new Set())

  const markCompleted = useCallback(
    (lessonId: string) => {
      if (inFlightRef.current.has(lessonId)) return
      inFlightRef.current.add(lessonId)
      setPendingLessonIds((prev) => new Set(prev).add(lessonId))

      onOptimistic(lessonId)

      const attempt = (isRetry: boolean): void => {
        api
          .put(`/academy/lessons/${lessonId}/progress`, { completed: true })
          .then(({ data }) => {
            const result = data?.data as PutProgressResponse | undefined
            inFlightRef.current.delete(lessonId)
            setPendingLessonIds((prev) => {
              const next = new Set(prev)
              next.delete(lessonId)
              return next
            })
            if (!result?.completed) {
              onRollback(lessonId)
              return
            }
            onConfirmed(lessonId, result.courses ?? [])
          })
          .catch(() => {
            if (!isRetry) {
              setTimeout(() => attempt(true), RETRY_DELAY_MS)
              return
            }
            inFlightRef.current.delete(lessonId)
            setPendingLessonIds((prev) => {
              const next = new Set(prev)
              next.delete(lessonId)
              return next
            })
            onRollback(lessonId)
            toast.error("No se pudo guardar tu progreso. Marca la lección como completada de nuevo para reintentar.")
          })
      }

      attempt(false)
    },
    [onOptimistic, onConfirmed, onRollback],
  )

  return { markCompleted, pendingLessonIds }
}
